// ============================================================
// The arena's shape and size - on the bar and in the editor alike
// ============================================================

import common from '../../styles/common.module.css';
import styles from '../../styles/Battlefield.module.css';
import SliderInput from '../SliderInput';
import { ARENA_SHAPES, arenaShapeOf, arenaBoundary } from '../../engine/battlefield/arenaShapes';

/** The shape the arena is now, and the shapes on offer. */
export function arenaShapes(boundary) {
  const shapeKey = boundary ? arenaShapeOf(boundary) : 'square';
  // Custom is a state you draw your way into, not an option to select.
  return { shapeKey, options: ARENA_SHAPES.filter((s) => s.key !== 'custom' || shapeKey === 'custom') };
}

const halfWidth = (b) => b.radius * (b.scaleX ?? 1);

/**
 * The five arena sliders, as a table: what each reads off the boundary and
 * the boundary each value makes.
 *
 * Half-width preserves the ASPECT, not the absolute depth: the size slider
 * has always grown the arena whole, and three E2E scaffolds and every
 * reflex depend on it.
 */
export const ARENA_SLIDERS = [
  {
    id: 'arena-size',
    label: 'Half-width',
    name: 'Arena size',
    min: 4,
    max: 200,
    unit: 'm',
    read: (b) => Math.round(halfWidth(b)),
    edit: (b, v) => ({ radius: Math.max(4, v || 4), scaleX: 1, scaleZ: (b.scaleZ ?? 1) / (b.scaleX ?? 1) }),
  },
  {
    id: 'arena-depth',
    label: 'Half-depth',
    name: 'Arena depth',
    min: 4,
    max: 200,
    unit: 'm',
    read: (b) => Math.round(b.radius * (b.scaleZ ?? 1)),
    edit: (b, v) => {
      const w = Math.max(4, halfWidth(b));
      return { radius: w, scaleX: 1, scaleZ: Math.max(4, v || 4) / w };
    },
  },
  {
    id: 'arena-rotation',
    label: 'Rotation',
    name: 'Arena rotation',
    min: 0,
    max: 355,
    step: 5,
    unit: 'deg',
    read: (b) => b.rotation ?? 0,
    edit: (b, v) => ({ rotation: v || 0 }),
  },
  {
    id: 'arena-offset-x',
    label: 'Offset X',
    name: 'Arena offset X',
    min: -200,
    max: 200,
    unit: 'm',
    read: (b) => Math.round(b.centre?.x ?? 0),
    edit: (b, v) => ({ centre: { x: v || 0, z: b.centre?.z ?? 0 } }),
  },
  {
    id: 'arena-offset-z',
    label: 'Offset Z',
    name: 'Arena offset Z',
    min: -200,
    max: 200,
    unit: 'm',
    read: (b) => Math.round(b.centre?.z ?? 0),
    edit: (b, v) => ({ centre: { x: b.centre?.x ?? 0, z: v || 0 } }),
  },
];

/** The arena's shape picker. */
export function ArenaShapeField({ build, boundary }) {
  const { shapeKey, options } = arenaShapes(boundary);
  return (
    <label className={styles.buildField}>
      Shape
      <select
        className={common.input}
        aria-label="Arena shape"
        value={shapeKey}
        onChange={(e) => build.setBoundary(arenaBoundary(boundary, { shape: e.target.value }))}
      >
        {options.map((s) => (
          <option key={s.key} value={s.key}>
            {s.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * The size, rotation and offset sliders - not for a custom ring, which is
 * drawn corner by corner. `prefix` tells the bar's test ids from the
 * editor's (`terrain-`).
 */
export function ArenaSliders({ build, boundary, prefix = '' }) {
  if (!boundary || arenaShapes(boundary).shapeKey === 'custom') return null;
  return ARENA_SLIDERS.map((s) => (
    <SliderInput
      key={s.id}
      label={s.label}
      name={s.name}
      data-testid={`${prefix}${s.id}`}
      min={s.min}
      max={s.max}
      step={s.step}
      value={s.read(boundary)}
      onChange={(value) => build.setBoundary(arenaBoundary(boundary, s.edit(boundary, value)))}
      unit={s.unit}
    />
  ));
}
